'use memo'

import { useContext, useState, useEffect, useMemo } from 'react'
import {
  AgxRow,
  AgxBodyText,
  AgxButton,
  AgxToast,
  cleanFullAddress,
  PropertyAddress,
} from '@urbanx/agx-ui-components'
import { isEqualWith } from 'lodash-es'
import { ContentLayout, PageLayout } from 'layout'
import { MenuTitles } from 'constants/menu'

import { useMutation } from '@tanstack/react-query'
import { SetCampaignOwnership } from 'services'
import { IPermission } from 'types/agency'
import { AgxToastState } from 'types/commonTypes'
import { FormPrompt } from 'components/FormPrompt'
import { isDirtyCompare } from 'utils/compares'
import {
  useAgentsInAgency,
  useSelectedAgency,
  useSelectedCampaign,
} from 'hooks'
import AgentsSelector from './AgentsSelector'
import { AuthContext } from '@urbanx/agx.auth.client'
import { AgxDivider, AgxSpacer } from '@urbanx/agx.ui-components2'
import WithdrawListingAction from '../WithdrawListingAction'

const Permissions = () => {
  const { userAccount, getAuthToken } = useContext(AuthContext)

  const { agency: selectedAgency } = useSelectedAgency()
  const { campaign: selectedCampaign, isLoading: isLoadingCampaign } =
    useSelectedCampaign()
  const { data: agents, isPending: isPendingAgents } = useAgentsInAgency()

  const [isFormDirty, setIsFormDirty] = useState(false)
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  })

  const initialPermission = useMemo<IPermission>(
    () => ({
      leadAgentId: selectedCampaign?.leadAgentId,
      secondAgentId: selectedCampaign?.secondAgentId,
      campaignId: selectedCampaign?.campaignId,
      createdByUserId: selectedCampaign?.createdByUserId,
      assignedAgentIds: selectedCampaign?.assignedAgentIds ?? [],
    }),
    [selectedCampaign]
  )
  const [permission, setPermission] = useState<IPermission>()

  useEffect(() => {
    setPermission(initialPermission)
  }, [initialPermission])

  useEffect(() => {
    setIsFormDirty(!isEqualWith(initialPermission, permission, isDirtyCompare))
  }, [permission])

  const { mutate: updateCampaignItems } = useMutation({
    mutationFn: SetCampaignOwnership,
    onSuccess: () => {
      setIsFormDirty(false)
      updateToastState({
        color: 'success',
        message: 'Role saved successfully',
        open: true,
      })
    },
  })

  const saveChanges = () => {
    updateCampaignItems({
      campaignId: selectedCampaign?.campaignId || '',
      leadAgentId: permission?.leadAgentId ?? '',
      secondAgentId: permission?.secondAgentId ?? '',
      assignedAgents: permission?.assignedAgentIds ?? [],
      getAuthToken,
    })
  }

  const activeAgents = useMemo(() => {
    return agents?.filter((a) => !a.softDeleted) || []
  }, [agents])

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={`Campaigns / ${cleanFullAddress(
        selectedCampaign?.address as PropertyAddress
      )}`}
      isCampaignsSection={true}
    >
      <FormPrompt hasUnsavedChanges={isFormDirty} />
      <AgxToast selector="#agxToast" toastState={toastState} />
      <ContentLayout
        hasSecondaryMenu={false}
        secondaryActiveMenu={MenuTitles.PERMISSIONS}
        isCampaigns={true}
        secondaryActions={<WithdrawListingAction />}
      >
        <AgxSpacer fill gap="spacing-3xl" direction="vertical">
          <AgxSpacer
            id="subPageHeadingContainer"
            fill
            gap="sm"
            direction="vertical"
          >
            <AgxRow spaceBetween>
              <AgxRow largeGap>
                <AgxBodyText large>Edit Role</AgxBodyText>
              </AgxRow>
              <AgxRow largeGap>
                <AgxButton
                  text="Save Changes"
                  medium
                  primary
                  onClick={() => saveChanges()}
                />
              </AgxRow>
            </AgxRow>
            <AgxDivider variant="horizontal" pushMargin="spacing-3xl" />
          </AgxSpacer>
          <div className="panelContainer">
            {!isLoadingCampaign &&
              !isPendingAgents &&
              selectedCampaign &&
              activeAgents &&
              permission?.createdByUserId && (
                <AgentsSelector
                  key={`agentsSelector-${selectedCampaign.campaignId}`}
                  agents={activeAgents ?? []}
                  permission={permission}
                  setPermission={setPermission}
                />
              )}
          </div>
        </AgxSpacer>
      </ContentLayout>
    </PageLayout>
  )
}

export default Permissions
